// Icon lookup for condition types. Maps each node type to the marker icon
// rendered by Pluma's ConditionViewerItem.
// Matches ~/code/ui condition-editor-v2 node views.

import type { IconName } from '@customerio/pluma-icons';
import { getChannelIcon } from '@/services/messaging-channels';
import type { AttributeSource, ConditionNode, MessageChannel } from '../types';

const NODE_TYPE_ICONS: Record<ConditionNode['type'], IconName> = {
  group: 'filter',
  attribute: 'person',
  event: 'trigger-event',
  segment: 'segment',
  device: 'mobile',
  pageview: 'link',
  screenview: 'modal-bottom',
  form: 'form',
  message: 'email',
  inbound: 'sms',
  opt_out: 'sms',
  relationship: 'connections',
  geoset: 'geofences',
  unknown: 'critical',
};

// WhatsApp and LINE conditions draw the SMS marker, as Ember's condition editor does
// (`condition-editor-v2/nodes/action/view.gts`), not the brand icon the rest of the app
// draws for those channels. `message` is the any-channel condition, not a channel.
const CHANNEL_ICONS: Record<MessageChannel, IconName> = {
  email: getChannelIcon('email'),
  sms: getChannelIcon('sms'),
  push: getChannelIcon('push'),
  in_app: getChannelIcon('in_app'),
  inbox: getChannelIcon('inbox'),
  slack: getChannelIcon('slack'),
  webhook: getChannelIcon('webhook'),
  whatsapp: getChannelIcon('sms'),
  line: getChannelIcon('sms'),
  message: getChannelIcon('email'),
};

// An attribute read from anywhere but the person is marked by where the field
// comes from rather than by the person mark every other attribute carries.
// Matches Ember's `app/utils/condition-viewer-item/marker-icon.ts`.
const ATTRIBUTE_SOURCE_ICONS = {
  object: 'object',
  relationship: 'connections',
  event: 'trigger-event',
  webhook: 'webhook',
  journey: 'campaigns',
  newsletter: 'newsletter',
} satisfies Record<AttributeSource, IconName>;

export function getIconForNode(node: ConditionNode): IconName {
  if (node.type === 'message') {
    return CHANNEL_ICONS[node.channel] ?? 'email';
  }
  if (node.type === 'attribute' && node.source != null) {
    return ATTRIBUTE_SOURCE_ICONS[node.source];
  }
  return NODE_TYPE_ICONS[node.type] ?? 'error';
}
